import { hasParent, isStateTreeNode } from "@gadgetinc/mobx-quick-tree";
import type { InputProps } from "@gadgetinc/widgets";
import { Input, expandBorderColors } from "@gadgetinc/widgets";
import { mergeOverrides } from "baseui";
import type { Overrides } from "baseui/overrides";
import type { Theme } from "baseui/theme";
import { isNil, omit } from "lodash";
import { observer } from "mobx-react-lite";
import type { ForwardedRef } from "react";
import React, { useState } from "react";
import { ProblemSeverity } from "state-trees/src/problems/ProblemSeverity";
import { useInteractionPreferences } from "../../client-state-trees/workspace/useInteractionPreferences";
import type { BindingProps } from "./useBinding";
import { useBinding } from "./useBinding";
import type { NodeWithProblems } from "./useProblemMessages";
import { useBoundNodeHasProblems } from "./useProblemMessages";
import type { DisabledOnProdProps } from "./useDisableProd";

type BoundInputProps<Node extends NodeWithProblems> = InputProps &
  BindingProps<Node, string> & {
    dataTestId?: string;
    variant?: "primary" | "secondary";
    onAction?: () => void;
    hideProblemsUntilAction?: boolean;
  } & DisabledOnProdProps<Node>;

const BoundInputInner = React.forwardRef(
  <Node extends NodeWithProblems>(props: BoundInputProps<Node>, ref: ForwardedRef<HTMLInputElement>) => {
    const [value, setValue] = useBinding(props);
    const { error, severity } = useBoundNodeHasProblems(props);
    const preferences = useInteractionPreferences();
    const [hasActed, setHasActed] = useState(false);

    // NOTE: Pages currently doesn't respect the type of props.node, so we explicitly check that it's a state tree node
    const hideProblemsUntilAction = props.hideProblemsUntilAction ?? (isStateTreeNode(props.node) ? !hasParent(props.node) : false);
    const showProblems = hideProblemsUntilAction ? hasActed : true;

    const actionHandler = () => {
      setHasActed(true);
      if (props.onAction) props.onAction();
    };

    const inputStyles = ({ $theme, $isFocused }: { $theme: Theme; $isFocused: boolean }) => {
      if (showProblems && !$isFocused && severity) {
        return severity === ProblemSeverity.Warning
          ? {
              backgroundColor: !preferences.inDarkMode ? $theme.colors.warning100 : $theme.colors.warning700,
              color: !preferences.inDarkMode ? $theme.colors.warning700 : $theme.colors.warning100,
              "-webkit-text-fill-color": !preferences.inDarkMode ? $theme.colors.warning700 : $theme.colors.warning100,
            }
          : {
              backgroundColor: !preferences.inDarkMode ? $theme.colors.negative50 : $theme.colors.negative700,
              color: !preferences.inDarkMode ? $theme.colors.negative700 : $theme.colors.negative50,
              "-webkit-text-fill-color": !preferences.inDarkMode ? $theme.colors.negative700 : $theme.colors.negative50,
            };
      }
      return props.variant === "secondary" ? { backgroundColor: $theme.colors.backgroundPrimary } : {};
    };

    return (
      <Input
        inputRef={ref}
        clearable={false} // this disables the esc key from working on inputs so we set it to false by default :( no option in baseweb currently to turn off this behaviour
        clearOnEscape={false}
        {...omit(props, ["property", "getter", "setter"])}
        error={showProblems && error}
        value={isNil(value) ? "" : value}
        onChange={(event) => {
          setValue(event.currentTarget.value);
          actionHandler();
        }}
        onBlur={(event) => {
          actionHandler();
          if (props.onBlur) props.onBlur(event);
        }}
        overrides={mergeOverrides(
          {
            Root: {
              style: ({ $theme, $isFocused }: { $theme: Theme; $isFocused: boolean }) => {
                if (showProblems && !$isFocused && severity) {
                  return severity === ProblemSeverity.Warning
                    ? expandBorderColors(`${$theme.colors.warning400} !important`)
                    : expandBorderColors(`${$theme.colors.negative500} !important`); // !important because when an error changes it overwrites the styling of a warning, this makes sure that can't happen
                }
                return {};
              },
            },
            Input: {
              style: inputStyles,
            },
            StartEnhancer: {
              style: inputStyles,
            },
            EndEnhancer: {
              style: inputStyles,
            },
          },
          // TypeScript can't determine that InputOverrides interface is compatible with Overrides<any>
          // See https://github.com/microsoft/TypeScript/issues/15300
          props.overrides as Overrides<any> | undefined
        )}
      />
    );
  }
);

export const BoundInput = observer(BoundInputInner);
